import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Plus, Trash2, ToggleLeft, ToggleRight, Users, Clock, Search, X, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const TYPE_STYLES = {
    info: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6', label: 'Info' },
    success: { bg: 'rgba(34,197,94,0.1)', color: '#22c55e', label: 'Success' },
    warning: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', label: 'Warning' },
    danger: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444', label: 'Danger' },
};

function toDatetimeLocal(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Notification card with inline edit ───────────────────────────────────────
function NotificationCard({ notification, onDelete, onToggle, onEdit }) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        title: notification.title,
        message: notification.message,
        notificationType: notification.notification_type,
        expiresAt: toDatetimeLocal(notification.expires_at),
    });

    const typeStyle = TYPE_STYLES[notification.notification_type] || TYPE_STYLES.info;
    const isExpired = notification.expires_at && new Date(notification.expires_at) < new Date();

    const handleSave = async () => {
        if (!form.title.trim() || !form.message.trim()) return;
        setSaving(true);
        try {
            await onEdit(notification.id, {
                title: form.title,
                message: form.message,
                notificationType: form.notificationType,
                expiresAt: form.expiresAt || null,
            });
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setForm({
            title: notification.title,
            message: notification.message,
            notificationType: notification.notification_type,
            expiresAt: toDatetimeLocal(notification.expires_at),
        });
        setEditing(false);
    };

    const targetCount = Array.isArray(notification.target_user_ids)
        ? notification.target_user_ids.length
        : (() => { try { return JSON.parse(notification.target_user_ids || '[]').length; } catch { return 0; } })();

    return (
        <div style={{
            background: 'var(--bg-secondary)',
            border: `1px solid ${editing ? 'var(--accent-primary)' : notification.is_active && !isExpired ? 'var(--border-color)' : 'var(--bg-tertiary)'}`,
            borderRadius: 8, padding: '14px 16px', marginBottom: 10,
            opacity: editing ? 1 : notification.is_active && !isExpired ? 1 : 0.6,
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>

                    {/* Badges row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                        {editing ? (
                            <select
                                value={form.notificationType}
                                onChange={e => setForm(p => ({ ...p, notificationType: e.target.value }))}
                                style={{ ...inlineInputStyle, width: 'auto', padding: '3px 8px', fontSize: 12 }}
                            >
                                {Object.entries(TYPE_STYLES).map(([k, v]) => (
                                    <option key={k} value={k}>{v.label}</option>
                                ))}
                            </select>
                        ) : (
                            <span style={{
                                padding: '2px 8px', borderRadius: 4, fontSize: 11,
                                fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                                background: typeStyle.bg, color: typeStyle.color,
                            }}>
                                {typeStyle.label}
                            </span>
                        )}
                        <span style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 11,
                            background: 'var(--bg-tertiary)', color: 'var(--text-muted)',
                            display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                            <Users size={10} />
                            {notification.target_type === 'all' ? 'All traders' : `${targetCount} traders`}
                        </span>
                        {isExpired && !editing && (
                            <span style={{ fontSize: 11, color: 'var(--accent-danger)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Clock size={10} /> Expired
                            </span>
                        )}
                        <span style={{ fontSize: 11, color: notification.is_active ? 'var(--accent-success)' : 'var(--text-muted)', marginLeft: 'auto' }}>
                            {notification.read_count || 0} reads
                        </span>
                    </div>

                    {/* Title */}
                    {editing ? (
                        <input
                            value={form.title}
                            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                            maxLength={100}
                            placeholder="Title"
                            style={{ ...inlineInputStyle, fontWeight: 600, marginBottom: 8 }}
                        />
                    ) : (
                        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>
                            {notification.title}
                        </div>
                    )}

                    {/* Message */}
                    {editing ? (
                        <textarea
                            value={form.message}
                            onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                            maxLength={1000}
                            rows={3}
                            style={{ ...inlineInputStyle, resize: 'vertical', marginBottom: 8 }}
                        />
                    ) : (
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                            {notification.message}
                        </div>
                    )}

                    {/* Expiry */}
                    {editing ? (
                        <div style={{ flex: 1, minWidth: 160 }}>
                            <input
                                type="datetime-local"
                                value={form.expiresAt}
                                min={new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)}
                                onChange={e => setForm(p => ({ ...p, expiresAt: e.target.value }))}
                                style={{ ...inputStyle, colorScheme: 'dark', cursor: 'pointer' }}
                            />
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                                ⏱ Expiry date — optional. Notification hides from traders after this time. Must be at least 1 hour from now.
                            </div>
                        </div>
                    ) : (
                        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                            {notification.admin_name || 'Admin'} · {new Date(notification.created_at).toLocaleString()}
                            {notification.expires_at && ` · Expires ${new Date(notification.expires_at).toLocaleString()}`}
                        </div>
                    )}

                    {/* Save/cancel buttons */}
                    {editing && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                            <button type="button" onClick={handleCancel} style={cancelBtnStyle}>Cancel</button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={saving || !form.title.trim() || !form.message.trim()}
                                style={saveBtnStyle(saving || !form.title.trim() || !form.message.trim())}
                            >
                                {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Action icons */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                        onClick={() => editing ? handleCancel() : setEditing(true)}
                        title={editing ? 'Cancel edit' : 'Edit'}
                        style={iconBtnStyle}
                    >
                        <Pencil size={14} color={editing ? 'var(--accent-primary)' : 'var(--text-secondary)'} />

                    </button>
                    <button onClick={() => onToggle(notification)} title={notification.is_active ? 'Deactivate' : 'Activate'} style={iconBtnStyle}>
                        {notification.is_active
                            ? <ToggleRight size={18} color="var(--accent-success)" />
                            : <ToggleLeft size={18} color="var(--text-secondary)" />}
                    </button>
                    <button onClick={() => onDelete(notification)} title="Delete" style={iconBtnStyle}>
                        <Trash2 size={15} color="var(--accent-danger)" />
                    </button>
                </div>
            </div>
        </div>
    );
}

const iconBtnStyle = {
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-color)',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const inlineInputStyle = {
    width: '100%', padding: '7px 10px',
    background: 'var(--bg-tertiary)', color: 'var(--text-primary)',
    border: '1px solid var(--border-color)', borderRadius: 6,
    fontSize: 13, boxSizing: 'border-box',
};

// ── Trader multiselect ────────────────────────────────────────────────────────
function TraderMultiSelect({ selected, onChange }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [searching, setSearching] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [open, setOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const containerRef = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setShowAll(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const loadAllClients = async () => {
        if (allClients.length > 0) { setShowAll(true); setOpen(true); return; }
        setLoadingAll(true);
        try {
            const res = await api.get('/broker/clients', { params: { limit: 200 } });
            setAllClients(res.data?.data?.clients || []);
            setShowAll(true);
            setOpen(true);
        } catch {
            toast.error('Failed to load clients');
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            if (!showAll) setOpen(false);
            return;
        }
        setShowAll(false);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await api.get('/broker/clients', { params: { search: query, limit: 20 } });
                const clients = res.data?.data?.clients || [];
                setResults(clients.filter(c => !selected.some(s => s.id === c.id)));
                setOpen(true);
            } catch {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [query, selected, showAll]);

    const addTrader = (client) => {
        if (selected.some(s => s.id === client.id)) return;
        onChange([...selected, { id: client.id, name: `${client.first_name} ${client.last_name}`, email: client.email }]);
    };

    const removeTrader = (id) => onChange(selected.filter(s => s.id !== id));

    const dropdownList = showAll
        ? allClients.filter(c => !selected.some(s => s.id === c.id))
        : results;

    const isSelected = (id) => selected.some(s => s.id === id);

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            {selected.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {selected.map(s => (
                        <span key={s.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            background: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
                            color: 'var(--accent-primary)',
                            border: '1px solid color-mix(in srgb, var(--accent-primary) 25%, transparent)',
                        }}>
                            {s.name}
                            <button type="button" onClick={() => removeTrader(s.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <X size={11} color="var(--accent-primary)" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                    <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onFocus={() => { if (query.trim()) setOpen(true); }}
                        placeholder="Search traders by name or email…"
                        style={{ ...inputStyle, paddingLeft: 30 }}
                    />
                    {searching && (
                        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--text-muted)' }}>
                            searching…
                        </span>
                    )}
                </div>
                <button type="button" onClick={loadAllClients} disabled={loadingAll} style={{
                    padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-color)',
                    background: showAll ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' : 'var(--bg-tertiary)',
                    color: showAll ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    cursor: loadingAll ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600,
                    whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                    <Users size={13} />
                    {loadingAll ? 'Loading…' : 'Browse all'}
                </button>
            </div>

            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                    borderRadius: 8, marginTop: 4, maxHeight: 240, overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}>
                    {showAll && dropdownList.length > 0 && (
                        <div style={{
                            padding: '8px 12px', borderBottom: '1px solid var(--border-color)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            position: 'sticky', top: 0, background: 'var(--bg-secondary)', zIndex: 1,
                        }}>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                {dropdownList.length} trader{dropdownList.length !== 1 ? 's' : ''} available
                            </span>
                            <button type="button"
                                onClick={() => {
                                    const toAdd = dropdownList.filter(c => !isSelected(c.id));
                                    onChange([...selected, ...toAdd.map(c => ({ id: c.id, name: `${c.first_name} ${c.last_name}`, email: c.email }))]);
                                }}
                                style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-primary)', background: 'none', border: 'none', cursor: 'pointer' }}>
                                Select all
                            </button>
                        </div>
                    )}

                    {dropdownList.length > 0 ? dropdownList.map(c => (
                        <button key={c.id} type="button" onClick={() => addTrader(c)}
                            style={{
                                width: '100%', textAlign: 'left', padding: '9px 12px',
                                background: 'none', border: 'none', cursor: 'pointer',
                                borderBottom: '1px solid var(--border-color)',
                                display: 'flex', alignItems: 'center', gap: 10,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >
                            <span style={{
                                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                                border: `2px solid ${isSelected(c.id) ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                                background: isSelected(c.id) ? 'var(--accent-primary)' : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {isSelected(c.id) && <span style={{ color: '#fff', fontSize: 10, fontWeight: 900 }}>✓</span>}
                            </span>
                            <span style={{ flex: 1 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', display: 'block' }}>
                                    {c.first_name} {c.last_name}
                                </span>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.email}</span>
                            </span>
                        </button>
                    )) : (
                        <div style={{ padding: '12px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
                            {query.trim() ? `No traders found for "${query}"` : 'No traders available'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Create form ───────────────────────────────────────────────────────────────
function CreateForm({ onSave, onCancel, loading }) {
    const [form, setForm] = useState({
        title: '', message: '', notificationType: 'info',
        targetType: 'all', expiresAt: '',
    });
    const [selectedTraders, setSelectedTraders] = useState([]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.message.trim()) return;
        if (form.targetType === 'specific' && selectedTraders.length === 0) {
            toast.error('Select at least one trader');
            return;
        }
        onSave({
            ...form,
            expiresAt: form.expiresAt || undefined,
            targetUserIds: form.targetType === 'specific' ? selectedTraders.map(t => t.id) : undefined,
        });
    };

    return (
        <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--accent-primary)',
            borderRadius: 8, padding: 16, marginBottom: 20,
        }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 12 }}>
                New Notification
            </div>
            <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gap: 12 }}>
                    <input
                        value={form.title}
                        onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="Title (max 100 chars)"
                        maxLength={100}
                        style={inputStyle}
                    />
                    <textarea
                        value={form.message}
                        onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                        placeholder="Message (max 1000 chars)"
                        maxLength={1000}
                        rows={3}
                        style={{ ...inputStyle, resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <select
                            value={form.notificationType}
                            onChange={e => setForm(p => ({ ...p, notificationType: e.target.value }))}
                            style={{ ...inputStyle, flex: 1, minWidth: 120 }}
                        >
                            {Object.entries(TYPE_STYLES).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                            ))}
                        </select>
                        <select
                            value={form.targetType}
                            onChange={e => {
                                setForm(p => ({ ...p, targetType: e.target.value }));
                                if (e.target.value === 'all') setSelectedTraders([]);
                            }}
                            style={{ ...inputStyle, flex: 1, minWidth: 140 }}
                        >
                            <option value="all">All Traders</option>
                            <option value="specific">Specific Traders</option>
                        </select>
                        <input
                            type="datetime-local"
                            value={form.expiresAt}
                            min={new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16)}
                            onChange={e => setForm(p => ({ ...p, expiresAt: e.target.value }))}
                            style={{ ...inlineInputStyle, flex: 1, colorScheme: 'dark', cursor: 'pointer' }}
                        />
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            ⏱ Expiry date — optional. Notification hides from traders after this time. Must be at least 1 hour from now.
                        </div>
                    </div>

                    {form.targetType === 'specific' && (
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Users size={13} />
                                Select Traders
                                {selectedTraders.length > 0 && (
                                    <span style={{
                                        padding: '1px 7px', borderRadius: 10, fontSize: 11,
                                        background: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)',
                                        color: 'var(--accent-primary)', fontWeight: 700,
                                    }}>
                                        {selectedTraders.length} selected
                                    </span>
                                )}
                            </div>
                            <TraderMultiSelect selected={selectedTraders} onChange={setSelectedTraders} />
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onCancel} style={cancelBtnStyle}>Cancel</button>
                    <button
                        type="submit"
                        disabled={loading || !form.title.trim() || !form.message.trim() || (form.targetType === 'specific' && selectedTraders.length === 0)}
                        style={saveBtnStyle(loading || !form.title.trim() || !form.message.trim() || (form.targetType === 'specific' && selectedTraders.length === 0))}
                    >
                        {loading ? 'Sending…' : `Send${form.targetType === 'specific' ? ` to ${selectedTraders.length} trader${selectedTraders.length !== 1 ? 's' : ''}` : ' to All'}`}
                    </button>
                </div>
            </form>
        </div>
    );
}

const inputStyle = {
    width: '100%', padding: '9px 12px',
    background: 'var(--bg-tertiary)', color: 'var(--text-primary)',
    border: '1px solid var(--border-color)', borderRadius: 8,
    fontSize: 14, boxSizing: 'border-box',
};
const cancelBtnStyle = {
    padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border-color)',
    background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13,
};
const saveBtnStyle = (disabled) => ({
    padding: '7px 16px', borderRadius: 6, border: 'none',
    background: disabled ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
    color: disabled ? 'var(--text-muted)' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
});

// ── Main panel ────────────────────────────────────────────────────────────────
export default function PlatformNotificationsPanel() {
    const [notifications, setNotifications] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [filter, setFilter] = useState('all');

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/broker/notifications', { params: { status: filter } });
            setNotifications(res.data.data.notifications);
            setTotal(res.data.data.total);
        } catch {
            toast.error('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

    const handleCreate = async (data) => {
        setSaving(true);
        try {
            await api.post('/broker/notifications', data);
            toast.success(
                data.targetType === 'specific'
                    ? `Notification sent to ${data.targetUserIds.length} trader${data.targetUserIds.length !== 1 ? 's' : ''}`
                    : 'Notification sent to all traders'
            );
            setShowForm(false);
            fetchNotifications();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to send');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async (id, data) => {
        try {
            await api.put(`/broker/notifications/${id}`, data);
            toast.success('Notification updated');
            fetchNotifications();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update');
            throw err;
        }
    };

    const handleDelete = async (n) => {
        if (!confirm('Delete this notification?')) return;
        try {
            await api.delete(`/broker/notifications/${n.id}`);
            toast.success('Deleted');
            fetchNotifications();
        } catch {
            toast.error('Failed to delete');
        }
    };

    const handleToggle = async (n) => {
        try {
            await api.put(`/broker/notifications/${n.id}`, { isActive: !n.is_active });
            fetchNotifications();
        } catch {
            toast.error('Failed to update');
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Bell size={16} color="var(--accent-primary)" />
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        Notifications {total > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({total})</span>}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                        value={filter}
                        onChange={e => setFilter(e.target.value)}
                        style={{ padding: '6px 10px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }}
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired / Inactive</option>
                    </select>
                    {!showForm && (
                        <button onClick={() => setShowForm(true)} style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                            background: 'var(--accent-primary)', color: '#fff', border: 'none',
                            borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        }}>
                            <Plus size={14} /> New
                        </button>
                    )}
                </div>
            </div>

            {showForm && (
                <CreateForm onSave={handleCreate} onCancel={() => setShowForm(false)} loading={saving} />
            )}

            {loading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>Loading…</div>
            ) : notifications.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32, fontSize: 14 }}>
                    No notifications yet. Send one to all your traders at once.
                </div>
            ) : (
                notifications.map(n => (
                    <NotificationCard
                        key={n.id}
                        notification={n}
                        onDelete={handleDelete}
                        onToggle={handleToggle}
                        onEdit={handleEdit}
                    />
                ))
            )}
        </div>
    );
}